import type { LotStage, MovementEventType, NurseryLot, NurseryStockItem } from '../types/nursery';
import type { PropagationFamily, PropagationMethod } from '../types/nurseryPropagation';

/**
 * What a run of propagation steps loses in all, as a percentage to one
 * decimal place. Survival multiplies, it does not add: 30% then 10% loses 37%,
 * not 40%. A step with no loss set counts as losing nothing; with none set at
 * all there is no figure.
 */
export function overallLossPct(stages: { expected_loss_pct: unknown }[]): number | null {
  const losses = stages.map((s) =>
    s.expected_loss_pct == null || s.expected_loss_pct === '' ? null : Number(s.expected_loss_pct));
  if (!losses.some((l) => l != null && Number.isFinite(l))) return null;
  const survival = losses.reduce<number>((acc, l) => acc * (1 - (l != null && Number.isFinite(l) ? l : 0) / 100), 1);
  return Math.round((1 - survival) * 1000) / 10;
}

/** What rooting moves a lot on to. Mirrors ROOTED_STAGE in HHNodeServer nurseryProductionService. */
export const ROOTED_STAGE: Partial<Record<LotStage, LotStage>> = {
  struck: 'rooted', deflasked: 'rooted', sown: 'seedling',
};

type LotLike = Pick<NurseryLot, 'cultivar_id' | 'rootstock_id' | 'tree_type_id' | 'current_stage'> & { is_mother?: boolean };
type ItemLike = Pick<NurseryStockItem, 'id' | 'cultivar_id' | 'rootstock_id' | 'tree_type_id' | 'active'>;

/**
 * The stock items a movement from this lot can make.
 *
 * Budding or grafting puts a new scion on the rootstock the trees stand on: a
 * rootstock lot is stored as cultivar = the rootstock, so the rootstock is
 * the lot's rootstock, else its cultivar. Anything else (pot up, line out,
 * deflask, harvest) keeps the plant and changes its form: same cultivar and
 * rootstock, any tree type. The server refuses what this leaves out.
 */
export function destinationItems<T extends ItemLike>(event: MovementEventType, lot: LotLike, items: T[]): T[] {
  const live = items.filter((i) => i.active && i.cultivar_id != null && i.tree_type_id != null);
  if (event === 'bud' || event === 'graft') {
    const under = lot.rootstock_id ?? lot.cultivar_id;
    return live.filter((i) => i.rootstock_id === under && i.cultivar_id !== lot.cultivar_id);
  }
  return live.filter((i) => i.cultivar_id === lot.cultivar_id && (i.rootstock_id ?? null) === (lot.rootstock_id ?? null));
}

/** The kinds of propagation each movement records. Every method fits a pot-up or a line-out. */
const FAMILIES_FOR_EVENT: Partial<Record<MovementEventType, PropagationFamily[]>> = {
  bud: ['bud'],
  graft: ['graft'],
  harvest: ['layer', 'division'],
  deflask: ['tissue_culture'],
  root: ['cutting', 'seed', 'tissue_culture'],
};

/** The organisation's methods that fit a movement, in use only. */
export function methodsFor(event: MovementEventType, methods: PropagationMethod[]): PropagationMethod[] {
  const families = FAMILIES_FOR_EVENT[event];
  return methods.filter((m) => m.active && (!families || families.includes(m.family)));
}

/** Movements that only make sense for some lots: rooting a struck or sown one, harvesting from a mother. */
export function eventFits(event: MovementEventType, lot: LotLike): boolean {
  if (event === 'root') return ROOTED_STAGE[lot.current_stage] != null;
  if (event === 'harvest') return !!lot.is_mother;
  return true;
}
